
import React from 'react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface EnhancedStatsCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  change?: string;
  positive?: boolean;
  className?: string;
  progress?: number;
  colorScheme?: 'primary' | 'blue' | 'green' | 'purple' | 'orange';
}

const colorSchemes = {
  primary: {
    icon: 'bg-primary/10 text-primary',
    glow: 'hover:shadow-primary/10',
    border: 'hover:border-primary/30'
  },
  blue: {
    icon: 'bg-blue-100 text-blue-600',
    glow: 'hover:shadow-blue-500/10',
    border: 'hover:border-blue-500/30'
  },
  green: {
    icon: 'bg-green-100 text-green-600',
    glow: 'hover:shadow-green-500/10',
    border: 'hover:border-green-500/30'
  },
  purple: {
    icon: 'bg-purple-100 text-purple-600',
    glow: 'hover:shadow-purple-500/10',
    border: 'hover:border-purple-500/30'
  },
  orange: {
    icon: 'bg-orange-100 text-orange-600',
    glow: 'hover:shadow-orange-500/10',
    border: 'hover:border-orange-500/30'
  }
};

export const EnhancedStatsCard = ({ 
  title, 
  value, 
  icon, 
  change, 
  positive, 
  className,
  progress,
  colorScheme = 'primary'
}: EnhancedStatsCardProps) => {
  const isMobile = useIsMobile();
  const colors = colorSchemes[colorScheme];
  
  return (
    <Card className={cn(
      "group relative overflow-hidden transition-all duration-500 cursor-pointer border border-border/50",
      "bg-gradient-to-br from-background via-background to-muted/10",
      "hover:shadow-lg hover:shadow-black/5 hover:-translate-y-1",
      "hover:bg-gradient-to-br hover:from-background hover:via-muted/5 hover:to-muted/20",
      "hover:border-border/80 hover:backdrop-blur-sm",
      colors.glow,
      className
    )}>
      {/* Subtle shimmer effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent shimmer-slide" />
      </div>
      
      <div className="relative p-4 md:p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs md:text-sm font-medium text-muted-foreground transition-colors duration-300">
              {title}
            </p>
            <h3 className={cn(
              "font-bold mt-1 transition-all duration-300",
              isMobile ? "text-lg" : "text-2xl"
            )}>
              {value}
            </h3>
            {change && (
              <div className="flex items-center gap-1 mt-1">
                <span className={cn(
                  "text-xs font-medium inline-flex items-center gap-1 transition-colors duration-300",
                  positive ? "text-green-600" : "text-red-500"
                )}>
                  {positive ? '↗' : '↘'} {change}
                </span>
              </div>
            )}
            {progress !== undefined && (
              <div className="mt-3">
                <Progress 
                  value={progress} 
                  className="h-1.5 bg-muted/30"
                />
                <span className="text-xs text-muted-foreground mt-1 block">
                  {progress}% utilization
                </span>
              </div>
            )}
          </div>
          {icon && (
            <div className={cn(
              "p-2 rounded-lg transition-all duration-300 group-hover:scale-105",
              colors.icon
            )}>
              {icon}
            </div>
          )}
        </div>
      </div>
      
      {/* Subtle border glow on hover */}
      <div className={cn(
        "absolute inset-0 border rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500",
        colors.border
      )} />
    </Card>
  );
};
